import { AzureDataLakeStoreCosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-cosmos-structured-stream.dataset';
import { AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset } from 'app/model/dataset/azure-data-lake-store-gen2-cosmos-structured-stream.dataset';
import { AzureTableDataset } from 'app/model/dataset/azure-table.dataset';
import { HttpServerLocation } from 'app/model/dataset/location/http-server.location';
import { RestResourceDataset } from 'app/model/dataset/rest-resource.dataset';
import { HttpServerLinkedService } from 'app/model/linkedservice/http-server.linkedservice';
import { RestServiceLinkedService } from 'app/model/linkedservice/rest-service.linkedservice';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { GetMetadataActivity } from 'app/model/pipeline/get-metadata.activity';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { isBinaryTypeDataset } from 'app/shared/utils/dataset-validation.util';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { generateUUID } from 'app/shared/utils/uuid.util';
import {
    Activity,
    Dataset,
    FormatDatasetV2,
    LakehouseLinkedService,
    LakehouseTableDataset,
    LinkedService,
    Location,
    Metamodel,
    ModelConstants,
    Owned,
    RootFolderType
} from 'shell-models-lib';

export function createDatasetWithLinkedService(
    linkedService: LinkedService,
    linkedServiceType: string,
    datasetOwner: Activity,
    services: {
        linkedServiceRegistry: LinkedServiceRegistryService;
        datasetRegistry: DatasetRegistryService;
    },
    options: {
        rootFolder: RootFolderType;
        isCopySink?: boolean;
        originalLocation?: Location;
        isOldM365Source?: boolean;
        supportedFileFormats?: string[];
        dmtsCredentialLinkedService?: LinkedService
    }
): {
    dataset: Dataset;
    format?: string;
} {
    if (linkedService == null) {
        return { dataset: null };
    }

    const { linkedServiceRegistry, datasetRegistry } = services;
    const { rootFolder, isCopySink, originalLocation, isOldM365Source, supportedFileFormats } = options;
    const metadata = linkedServiceRegistry.getLinkedServiceConfiguration(linkedServiceType);
    const datasetName = generateUUID().replaceAll('-', '_');
    let dataset;
    let sourceDatasetType;
    let format: string;
    if (metadata?.linkedServiceKind === 'file') {
        // for M365, only binary is supported as sink type. so default should be binary
        if (isOldM365Source && isCopySink) {
            format = ModelConstants.binaryDataset;
        } else if (linkedService instanceof LakehouseLinkedService && rootFolder === 'Tables') {
            if (
                datasetOwner instanceof CopyActivity ||
                datasetOwner instanceof LookupActivity ||
                datasetOwner instanceof GetMetadataActivity
            ) {
                // activity uses LakehouseTableDataset
                const dataset = createLakehouseTableDataset(datasetName, linkedService, datasetOwner);
                dataset.dmtsCredentialForArtifact = options?.dmtsCredentialLinkedService;
                return { dataset };
            }
            format = ModelConstants.deltaFormat;
        } else if (metadata.type === adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType) {
            dataset = Owned.create<AzureDataLakeStoreCosmosStructuredStreamFileDataset>(
                AzureDataLakeStoreCosmosStructuredStreamFileDataset.MODEL,
                datasetOwner
            );
            dataset.name = datasetName;
            dataset.linkedService = linkedService;
            dataset.isV2 = true;
            return { dataset };
        } else if (metadata.type === adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType) {
            dataset = Owned.create<AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset>(
                AzureDataLakeStoreGen2CosmosStructuredStreamFileDataset.MODEL,
                datasetOwner
            );
            dataset.name = datasetName;
            dataset.linkedService = linkedService;
            dataset.isV2 = true;
            return { dataset };
        } else if (
            isCopySink &&
            datasetOwner instanceof CopyActivity &&
            datasetOwner.sourceDataset?.isSampleDataset &&
            (sourceDatasetType = Metamodel.modelOf(datasetOwner.sourceDataset).name) &&
            supportedFileFormats?.includes(sourceDatasetType)
        ) {
            // If source is sample dataset and sink supports source dataset type, set sink as the same type with source.
            format = sourceDatasetType;
        } else if (
            (!supportedFileFormats || supportedFileFormats.includes(ModelConstants.binaryDataset)) &&
            (!(datasetOwner instanceof CopyActivity) ||
                (isCopySink &&
                    (datasetOwner.sourceDataset == null || isBinaryTypeDataset(datasetOwner.sourceDataset))) ||
                (!isCopySink && (datasetOwner.sinkDataset == null || isBinaryTypeDataset(datasetOwner.sinkDataset))))
        ) {
            // If one of the source and sink dataset is binary, the other one must be binary.
            format = ModelConstants.binaryDataset;
        } else {
            format = ModelConstants.delimitedTextDataset;
        }
        const datasetModel = Metamodel.models().find((model) => model.name === format);
        dataset = Owned.create<Dataset>(datasetModel, datasetOwner);
        dataset.name = datasetName;
        (dataset as FormatDatasetV2).updateLinkedService(linkedService, originalLocation, {
            linkedServiceType: metadata.type
        });
        if (linkedService instanceof LakehouseLinkedService) {
            (dataset as FormatDatasetV2).rootFolder = rootFolder;
        }
    } else if (metadata.type === adfModelTypeMap.azureTable.linkedServiceType) {
        dataset = Owned.create<AzureTableDataset>(AzureTableDataset.MODEL, datasetOwner);
        dataset.name = datasetName;
        dataset.linkedService = linkedService;
    } else {
        const datasetType = datasetRegistry
            .getAllPrimaryAndAvailableDatasetConfigurations()
            .find(
                (v) =>
                    v.supportLinkedServiceTypes &&
                    v.supportLinkedServiceTypes.length === 1 &&
                    v.supportLinkedServiceTypes[0] === metadata.type
            )?.type;
        if (datasetType) {
            const datasetModel = Metamodel.modelByName(datasetType);
            dataset = Owned.create<Dataset>(datasetModel, datasetOwner);
            dataset.name = datasetName;
            dataset.linkedService = linkedService;
        }
    }
    if (dataset) {
        dataset.isV2 = true;
        dataset.dmtsCredentialForArtifact = options?.dmtsCredentialLinkedService;
        seedDatasetRelativeUrlFromUploadedSource(dataset, linkedService);
    }

    return {
        dataset,
        format
    };
}

/**
 * If the LS has an uploaded-source absolute URL stashed by `UnifiedPQConnectionService`,
 * consume it and seed the freshly-created dataset's `relativeUrl` so the user does not
 * have to retype it.
 *
 * Centralized here because `createDatasetWithLinkedService` is the chokepoint every
 * code path uses to produce the final dataset bound to an LS (incl. the
 * `activity-inline-dataset.onLinkedServiceTypeChange` path that recreates the dataset
 * when the dialog reports `linkedServiceType: 'HttpServer'` for a `RestService` LS
 * instance). Writing in `trident-connection-select` is too early because this code
 * runs afterwards and replaces the dataset.
 *
 * - `FormatDatasetV2` with `HttpServerLocation` → `location.relativeUrl`
 * - `RestResourceDataset` → `dataset.relativeUrl`
 * - Only writes when the destination field is empty so we never clobber user input.
 * - No-op for any other dataset/LS combination.
 */
export function seedDatasetRelativeUrlFromUploadedSource(
    dataset: Dataset,
    linkedService: LinkedService
): void {
    if (dataset == null) {
        return;
    }
    if (
        !(linkedService instanceof HttpServerLinkedService) &&
        !(linkedService instanceof RestServiceLinkedService)
    ) {
        return;
    }
    const relativeUrl = linkedService.consumeRelativeUrlFromUploadedFullUrl();
    if (stringIsNullOrEmpty(relativeUrl)) {
        return;
    }
    if (dataset instanceof RestResourceDataset) {
        if (stringIsNullOrEmpty(dataset.relativeUrl)) {
            dataset.relativeUrl = relativeUrl;
        }
        return;
    }
    if (dataset instanceof FormatDatasetV2 && dataset.location instanceof HttpServerLocation) {
        if (stringIsNullOrEmpty(dataset.location.relativeUrl)) {
            dataset.location.relativeUrl = relativeUrl;
        }
    }
}

export function createLakehouseTableDataset(
    name: string,
    ls: LakehouseLinkedService,
    owner: Owned,
    tableName?: string
): LakehouseTableDataset {
    const dataset = Owned.create<LakehouseTableDataset>(LakehouseTableDataset.MODEL, owner);
    dataset.name = name;
    dataset.linkedService = ls;
    dataset.isV2 = true;
    dataset.table = tableName;
    return dataset;
}
